/** @format */

import {useState} from 'react';

import {add, endOfDay, startOfDay} from 'date-fns';

import {Discreteness, Period} from '../model';
import getPeriodDays from '../helpers/getPeriodDays';
import {getNewDates} from '../helpers/getNewDates';
import {
  DisableDiscretenessOptions,
  getDisableDiscreteness,
} from '../helpers/getDisableDiscreteness';

export interface PeriodSelectorOptions {
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  defaultPeriod?: Period;
  defaultDiscreteness?: Discreteness;
  now?: Date;
}

interface PeriodSelectorReturn {
  startDate: Date;
  endDate: Date;
  activePeriod: Period;
  activeDiscreteness: Discreteness;
  onChangeStartDate: (value: Date) => void;
  onChangeEndDate: (value: Date) => void;
  onChangePeriod: (value: Period) => void;
  onChangeDiscreteness: (value: Discreteness) => void;
  onSwipePeriod: (value: number) => void;
}

const usePeriodSelector = (options?: PeriodSelectorOptions): PeriodSelectorReturn => {
  const defaultPeriod = options?.defaultPeriod || 'month';
  const defaultDiscreteness: Discreteness = options?.defaultDiscreteness || 'day';
  const newDates = getNewDates({
    period: defaultPeriod,
    defaultStartDate: options?.defaultStartDate,
    defaultEndDate: options?.defaultEndDate,
    now: options?.now,
  });

  const [startDate, setStartDate] = useState<Date>(newDates.startDate);
  const [endDate, setEndDate] = useState<Date>(newDates.endDate);
  const [activePeriod, setActivePeriod] = useState<Period>(defaultPeriod);
  const [activeDiscreteness, setActiveDiscreteness] = useState<Discreteness>(defaultDiscreteness);

  // Каждый раз при изменении дат нужно запускать метод обновления дискретности
  // Если текущая дискретность заблокирована, то переключаем её на days
  const updateDiscreteness = (options: DisableDiscretenessOptions) => {
    const disabled = getDisableDiscreteness(options);
    if (disabled) {
      setActiveDiscreteness('day');
    }
  };

  const onChangePeriod = (period: Period) => {
    setActivePeriod(period);

    if (period === 'custom') {
      return;
    }

    const updatedDates = getNewDates({
      period,
    });

    setStartDate(updatedDates.startDate);
    setEndDate(updatedDates.endDate);

    updateDiscreteness({
      startDate: updatedDates.startDate,
      endDate: updatedDates.endDate,
      currentKey: activeDiscreteness,
    });
  };

  const onChangeDiscreteness = (discreteness: Discreteness) => {
    setActiveDiscreteness(discreteness);
  };

  const onChangeStartDate = (value: Date) => {
    const newStartDate = startOfDay(value);
    setStartDate(newStartDate);
    updateDiscreteness({
      startDate: newStartDate,
      endDate,
      currentKey: activeDiscreteness,
    });
  };

  const onChangeEndDate = (value: Date) => {
    const newEndDate = endOfDay(value);
    setEndDate(newEndDate);
    updateDiscreteness({
      startDate: startDate,
      endDate: newEndDate,
      currentKey: activeDiscreteness,
    });
  };

  const onSwipePeriod = (value: number) => {
    const constant = getPeriodDays(activePeriod, new Date());

    const newStartDate = add(startDate, {days: value === -1 ? -constant : +constant});
    const newEndDate = add(endDate, {days: value === -1 ? -constant : +constant});
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    updateDiscreteness({
      startDate: newStartDate,
      endDate: newEndDate,
      currentKey: activeDiscreteness,
    });
  };

  return {
    activeDiscreteness,
    startDate,
    endDate,
    activePeriod,
    onChangePeriod,
    onSwipePeriod,
    onChangeStartDate,
    onChangeEndDate,
    onChangeDiscreteness,
  };
};

export {usePeriodSelector};
